import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Inject, Injectable } from '../../injection';
import { EmptyResponse } from '../data/empty-response.do';
import { Organization, OrganizationEagerLoad } from '../data/organization.do';
import { PagedResults } from '../data/paged-results.model';
import { ApiEndpointConfiguration } from '../interfaces/api-endpoint-configuration.interface';
import { Dictionary, ParameterValueType } from '../interfaces/dictionary.interface';
import { IMobizHttpService } from '../interfaces/mobiz-http.interface';
import { FlagsEnum } from '../other/flags-enum';
import { MobizServerApi } from '../other/mobiz-server-api.enum';
import { IOrganizationService, OrganizationQueryParameters } from './organization.interface';

@Injectable()
export class OrganizationService implements IOrganizationService {
  constructor(
    @Inject(IMobizHttpService) private http: IMobizHttpService) {
  }

  /**
   * @inheritdoc
   */
  public createOrganization(organization: Organization): Observable<Organization> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllOrganizations,
      body: JSON.stringify(organization),
    };

    return this.http.post(new Organization(), config);
  }

  /**
   * @inheritdoc
   */
  public deleteOrganization(organizationId: string): Observable<EmptyResponse> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.Organization,
      urlParameters: {
        organizationId: organizationId,
      },
    };

    return this.http.delete(new Organization(), config);
  }

  /**
   * @inheritdoc
   */
  public getOrganizations(
    queryParameters: OrganizationQueryParameters,
    eagerLoad?: FlagsEnum<OrganizationEagerLoad>,
  ): Observable<PagedResults<Organization>> {
    const reqQueryParameters = { ...queryParameters };

    if (eagerLoad) {
      reqQueryParameters.eagerLoad = eagerLoad.value;
    }

    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllOrganizations,
      queryParameters: reqQueryParameters,
    };
    let pagedOrganization = new PagedResults<Organization>(Organization);
    return this.http.get(pagedOrganization, config).pipe(
      map((response: PagedResults<Organization>): PagedResults<Organization> => {
        return response;
      }),
    );
  }

  /**
   * @inheritdoc
   */
  public getOrganization(organizationId: string, eagerLoad?: FlagsEnum<OrganizationEagerLoad>): Observable<Organization> {
    let queryParameters: Dictionary<ParameterValueType> = {};
    if (eagerLoad) {
      queryParameters = {
        eagerLoad: eagerLoad.value
      };
    }

    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.Organization,
      urlParameters: {
        organizationId: organizationId,
      },
      queryParameters: queryParameters
    };

    return this.http.get(new Organization(), config);
  }

  /**
   * @inheritdoc
   */
  public updateOrganization(organization: Organization): Observable<Organization> {
    let config: ApiEndpointConfiguration = {
      body: JSON.stringify(organization),
      serverApi: MobizServerApi.Organization,
      urlParameters: {
        organizationId: organization.id,
      },
    };

    return this.http.put(new Organization(), config);
  }

}
